
import React from 'react';
import { getAuthor } from '../../../mocks/authors';

interface AuthorBioProps {
  authorName: string;
  articleDate: string;
}

/**
 * AuthorBio component renders a short biography for a given author.
 * It safely handles missing author data and includes basic error handling.
 */
const AuthorBio: React.FC<AuthorBioProps> = ({ authorName, articleDate }) => {
  // Try to fetch the author; if not found, render nothing (or you could render a fallback UI)
  const author = React.useMemo(() => {
    try {
      return getAuthor(authorName);
    } catch (e) {
      console.error('Failed to fetch author:', e);
      return null;
    }
  }, [authorName]);

  if (!author) {
    // Optionally, you could render a placeholder or an error message here
    return null;
  }

  return (
    <div className="bg-gray-50 rounded-2xl p-6 mt-12 border border-gray-100">
      <div className="flex items-start gap-5">
        <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
          <img
            src={author.avatar}
            alt={`${author.name} - ${author.title}`}
            title={author.name}
            className="w-full h-full object-cover object-top"
            width={80}
            height={80}
            loading="lazy"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base font-bold text-gray-900">{author.name}</span>
            {author.credentials.map((c) => (
              <span
                key={c}
                className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full whitespace-nowrap"
              >
                {c}
              </span>
            ))}
          </div>

          <p className="text-sm text-teal-600 font-medium mb-2">{author.title}</p>
          <p className="text-sm text-gray-600 leading-relaxed mb-3">{author.bio}</p>

          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span>Published: {articleDate}</span>
            <span>&middot;</span>
            <span>Last updated: January 2026</span>

            {author.social?.twitter && (
              <>
                <span>&middot;</span>
                <a
                  href={author.social.twitter}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  {/* Using an <i> tag for the icon; ensure the icon library is loaded globally */}
                  <i className="ri-twitter-x-line" aria-hidden="true"></i>
                  <span className="sr-only">Twitter</span>
                </a>
              </>
            )}

            {author.social?.linkedin && (
              <>
                <span>&middot;</span>
                <a
                  href={author.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-linkedin-line" aria-hidden="true"></i>
                  <span className="sr-only">LinkedIn</span>
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorBio;
